/**
 * Redux witness watchers for content
 * @module services/content
 */
import { List, Map, Set } from 'immutable'
import { map as mapPromise } from 'bluebird'
import {
  CONTENT_REFERENCE_FIELDS,
  CONTENT_REFERENCE_FIELDS_ASYNC,
} from '../content-references/types'
import { getContentReferenceTypesContentDictionary } from '../content-references'
import { CONTENT_POLICY_STORE_KEY } from '../content-policy'
import { MEDIA_STORE_KEY } from '../media'
import { SET_PAGE_READY } from '../pages/redux/actions'
import { isLoggedIn } from '../auth'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { isPageReady } from '../pages'
import { SET_LOCATION_DATA } from '../location/redux/actions'
import {
  createViewRecords, findView, getApplicationDataByKey, getViewRecordsAsList,
} from '../data'
import { STORE_KEY_IMAGES } from '../images/store-keys'
import {
  SET_DATA_APPLICATION_BY_KEY,
  GET_DATA_LIST,
  getDataList as getDataListAction,
  setDataList as setDataListAction,
  PERSIST_DATA,
  setDataApplicationByKey as setDataApplicationByKeyAction,
  deleteDataApplicationByKey as deleteDataApplicationByKeyAction,
  resetData as resetDataAction,
  SET_DATA_LIST,
} from '../data/redux/actions'
import { getConfigDataServer } from '../config'
import { renderModal as renderModalAction } from '../modal/redux/actions'
import { ERRORS_MODAL, FETCHING_MODAL } from '../modal'
import { showErrorToast as showErrorToastAction, showSuccessToast as showSuccessToastAction } from '../toast/redux/actions'
import { getFirstErrorMessage } from '../error'
import {
  isContentListPage,
  isContentDetailPage,
  CONTENT_STORE_KEY,
  CONTENT_PATH_STORE_KEY,
  createContent,
  getContentList,
  createContentListViewOptions,
  createContentDetailViewOptions,
  createContentImagesRelationshipViewOptions,
  getContentPathFromContentTitle,
  createContentPathCreateViewOptions,
  createContentCreateViewOptions,
  isContentCreatePage,
  CONTENT_CREATE_APPLICATION_KEY,
  createContentTranslation,
  createContentMediaOptions,
  updateContent,
  createContentTranslationsViewOptions,
  CONTENT_DETAIL_APPLICATION_KEY,
  createContentEpisodesOptions,
  isContentBulkPage,
  updateContentList,
  CONTENT_BULK_STATE_PROCESSED,
  // CONTENT_BULK_STATE_PROCESSING,
  CONTENT_BULK_STATE_UNPROCESSED,
  removeContent,
  fetchExistingTranslation,
} from '.'
import { CONTENT_TYPE_SERIES } from './content-types'
import { BULK_DELETE_CONTENT, DELETE_CONTENT, FETCHING_TRANSLATION } from './redux/actions'

/**
 * Returns true if the action has a storeKey of 'content'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isContentDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === CONTENT_STORE_KEY
}

/**
 * Returns true if the action has a storeKey of 'content' and applicationKey of 'detail'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @param {String} [options.action.payload.applicationKey] The application key
 * @returns {Boolean}
 */
function isContentDetailDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { applicationKey, storeKey } = {} } = action
  return storeKey === CONTENT_STORE_KEY && applicationKey === CONTENT_DETAIL_APPLICATION_KEY
}

export function initContentListPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ state, prevState, dispatch }) => {
      const { data } = state
      const storeKey = CONTENT_STORE_KEY
      const options = createContentListViewOptions(state)
      const prevOptions = createContentListViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const contentReady = view && options.equals(prevOptions)
      if (contentReady) {
        return
      }
      dispatch(getDataListAction({
        storeKey,
        options,
      }))
    },
  )
    .when(({ state }) => {
      const { auth, page } = state
      return isLoggedIn(auth) && isPageReady({ page }) && isContentListPage(state)
    })
}

/**
 * Initialize content detail page
 * @param {*} param0 The params
 */
export function initContentDetailPage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = CONTENT_STORE_KEY
      const options = createContentDetailViewOptions(state)
      const prevOptions = createContentDetailViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const viewOptionsChanged = !view || !options.equals(prevOptions)
      // const content = getCurrentContentDetail({
      //   data,
      //   location,
      //   storeKey,
      //   applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
      // })
      if (options.get('id') && prevOptions.get('id') && options.get('id') !== prevOptions.get('id')) {
        dispatch(deleteDataApplicationByKeyAction({
          storeKey,
          applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
        }))
      }
      if (viewOptionsChanged) {
        dispatch(getDataListAction({
          storeKey,
          options,
        }))
      }
      dispatch(getDataListAction({
        storeKey: CONTENT_POLICY_STORE_KEY,
        options: Map({ limit: 500 }),
      }))
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isContentDetailPage(state)
  })
}

/**
 * Initialize content bulk upload page
 * @param {*} param0 The params
 */
export function initContentBulkPage ({ after }) {
  return after([
    SET_AUTH_DATA,
    SET_PAGE_READY,
    SET_LOCATION_DATA,
  ], (/* { dispatch, state, prevState } */) => {
    // console.log('dispatch: ', dispatch)
    // console.log('state: ', state)
    // console.log('prevState: ', prevState)
  }).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page })
      && isContentBulkPage(state)
  })
}

/**
 * Handle the get data list action for content detail page
 * @param {*} param0 The params
 */
export function setDataListContentDetailPage ({ after }) {
  return after([SET_DATA_LIST], ({ dispatch, state, action }) => {
    const { payload: { applicationKey = CONTENT_DETAIL_APPLICATION_KEY } } = action
    const { data } = state
    const storeKey = CONTENT_STORE_KEY
    const options = createContentDetailViewOptions(state)
    const record = getViewRecordsAsList({
      data,
      storeKey,
      options,
    }).first() || Map()
    const applicationData = getApplicationDataByKey({
      data, storeKey, applicationKey,
    }) || Map()
    const idsEqual = record.get('id') === applicationData.get('id')
    if (!record.isEmpty() && !idsEqual) {
      const contentReferenceFields = CONTENT_REFERENCE_FIELDS.get(record.get('contentType'), List())
      const contentReferenceFieldsAsync = CONTENT_REFERENCE_FIELDS_ASYNC.get(record.get('contentType'), List())
      dispatch(setDataApplicationByKeyAction({
        storeKey,
        applicationKey,
        applicationData: record,
      }))
      contentReferenceFields.filter(
        (f) => !contentReferenceFieldsAsync.includes(f),
      ).forEach((v) => {
        const contentReferenceOptions = getContentReferenceTypesContentDictionary().get(v)
        dispatch(getDataListAction({
          storeKey,
          options: Map({
            contentType: contentReferenceOptions.get('contentType'),
            bundle: contentReferenceOptions.get('bundle'),
            filterType: contentReferenceOptions.get('filterType'),
            primaryLanguageFallback: true,
            sort: 'alpha',
          }),
        }))
      })
    }
  }).when((options = {}) => {
    const { state } = options
    const { auth, page } = state
    return isLoggedIn(auth)
      && isPageReady({ page })
      && isContentDetailPage(state)
      && isContentDataAction(options)
  })
}

export function getDataListContent ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload
    try {
      const include = options.get('include') || Set()
      const contentList = await getContentList({
        auth,
        server: getConfigDataServer(config),
        offset: options.get('offset'),
        limit: options.get('limit'),
        bundle: List.isList(options.get('bundle')) ? options.get('bundle').toJS() : options.get('bundle'),
        id: options.get('id'),
        include: include.filter((v) => Set(['content_references', 'season_numbers', 'content_list_criteria']).includes(v)).toJS(),
        title: options.get('title'),
        mediaId: options.get('mediaId'),
        seasonNumber: options.get('seasonNumber'),
        published: options.get('published'),
        publishedAtDate: options.get('publishedAtDate'),
        content: List.isList(options.get('content')) ? options.get('content').toJS() : options.get('content'),
        contentId: List.isList(options.get('contentId')) ? options.get('contentId').toJS() : options.get('contentId'),
        contentType: List.isList(options.get('contentType')) ? options.get('contentType').toJS() : options.get('contentType'),
        filterType: List.isList(options.get('filterType')) ? options.get('filterType').toJS() : options.get('filterType'),
        language: List.isList(options.get('language')) ? options.get('language').toJS() : options.get('language'),
        referencesTo: List.isList(options.get('referencesTo')) ? options.get('referencesTo').toJS() : options.get('referencesTo'),
        primaryLanguageFallback: options.get('primaryLanguageFallback'),
        sort: options.get('sort'),
        sortDirection: options.get('sortDirection'),
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(contentList.get('content')),
        total: contentList.get('totalCount'),
        processing: false,
      }))
      contentList.get('content').forEach((v) => {
        // Get get images
        if (include.includes('images')) {
          dispatch(getDataListAction({
            storeKey: STORE_KEY_IMAGES,
            options: createContentImagesRelationshipViewOptions(v),
          }))
        }
      })
      if (include.includes('content_translations')) {
        const content = contentList.get('content')
        if (!content.isEmpty()) {
          dispatch(getDataListAction({
            storeKey: CONTENT_STORE_KEY,
            options: createContentTranslationsViewOptions(content),
          }))
        }
      }
      if (include.includes('content_references')) {
        // Get content media ids
        const content = contentList.get('content').flatMap((v) => v.get('references', List()).map((vr) => Map({
          contentId: vr.get('contentId'),
          contentType: vr.get('contentType'),
        }))).toSet().toList()
        if (!content.isEmpty()) {
          dispatch(getDataListAction({
            storeKey,
            options: Map({ content, primaryLanguageFallback: true }),
          }))
        }
      }
      if (include.includes('media')) {
        // Get content media ids
        const mediaOptionsList = contentList.get('content').map((v) => createContentMediaOptions({ content: v }))
        const mediaId = mediaOptionsList.flatMap((v) => v.get('mediaId')).filter((v) => v).toSet()
        if (!mediaId.isEmpty()) {
          dispatch(getDataListAction({
            storeKey: MEDIA_STORE_KEY,
            options: Map({
              mediaId: mediaOptionsList.flatMap((v) => v.get('mediaId')).filter((v) => v).toSet(),
              include: mediaOptionsList.first().get('include'),
            }),
          }))
        }
      }
      if (include.includes('episodes')) {
        // Get the episodes by season
        const content = contentList.get('content')
        if (!content.isEmpty() && content.some((v) => v.get('contentType') === CONTENT_TYPE_SERIES)) {
          dispatch(getDataListAction({
            storeKey: CONTENT_STORE_KEY,
            options: createContentEpisodesOptions({ content }),
          }))
        }
      }
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isContentDataAction)
}

export function deleteContent ({ after }) {
  return after(DELETE_CONTENT, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, contentId } = action.payload

    dispatch(setDataListAction({
      storeKey,
      processing: true,
    }))
    try {
      await removeContent({
        auth,
        server: getConfigDataServer(config),
        contentId,
      })
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        processing: false,
        error: e,
      }))
    }
  })
}

// TODO: maybe it would be greate to have bulk delete endpoint on BE side
export function deleteContentBulk ({ after }) {
  return after(BULK_DELETE_CONTENT, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, contentIds } = action.payload

    dispatch(setDataListAction({
      storeKey,
      processing: true,
    }))
    try {
      await mapPromise(contentIds, async (contentId) => removeContent({
        auth,
        server: getConfigDataServer(config),
        contentId,
      }))
      window.location.reload()
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        processing: false,
        error: e,
      }))
    }
  })
}

export function fetchTranslation ({ after }) {
  return after(FETCHING_TRANSLATION, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, content } = action.payload

    dispatch(setDataListAction({
      storeKey,
      processing: true,
    }))

    dispatch(
      renderModalAction(FETCHING_MODAL, {
        innerClass: 'modal__fetching',
        backgroundDismiss: false,
        hideDismiss: true,
        title: 'Fetching Existing Translation',
      }),
    )

    try {
      await fetchExistingTranslation({
        auth,
        server: getConfigDataServer(config),
        content,
      })
      window.location.reload()
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        processing: false,
        error: e,
      }))
      dispatch(
        renderModalAction(ERRORS_MODAL, {
          innerClass: 'modal-errors',
          backgroundDismiss: false,
          errors: [e],
        }),
      )
    }
  })
}

/**
 * Persist data for content create to backend
 */
export function persistDataContent ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload
    let { record } = action.payload
    try {
      const isCreate = options.get('create')
      record = record.update((v) => {
        // Apply missing primary language using language
        if (isCreate && !v.get('primaryLanguage')) {
          return v.set('primaryLanguage', v.get('language'))
        }
        return v.update('references', List(), (refs) => refs.filter((ref) => ref.has('contentId') && ref.has('contentType')))
      })
      if (isCreate) {
        if (!record.get('path')) {
          const pathData = await getContentPathFromContentTitle({
            auth,
            server: getConfigDataServer(config),
            data: record,
          })
          record = record.set('path', pathData.get('path'))
        }
      }
      let content
      if (isCreate) {
        content = await createContent({
          auth,
          server: getConfigDataServer(config),
          data: record,
          generatePath: options.get('generatePath'),
          createTranslationJob: options.get('createTranslationJob'),
        })
      } else {
        content = await updateContent({
          auth,
          server: getConfigDataServer(config),
          data: record,
          generatePath: options.get('generatePath'),
        })
        if (options.get('detail')) {
          dispatch(setDataApplicationByKeyAction({
            storeKey,
            applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
            applicationData: content,
          }))
        }
      }
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(List([content])),
        total: 1,
        processing: false,
        error: null, // clear out any errors from previous failed requests
      }))
      if (isCreate) {
        dispatch(deleteDataApplicationByKeyAction({
          storeKey,
          applicationKey: CONTENT_CREATE_APPLICATION_KEY,
        }))
        dispatch(resetDataAction({
          storeKey: CONTENT_PATH_STORE_KEY,
        }))
      }
      dispatch(showSuccessToastAction('Saved successfully'))
    } catch (e) {
      dispatch(showErrorToastAction(getFirstErrorMessage(e, 'Save failed. ')))
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  }).when((options = {}) => {
    const { state } = options
    return isContentDataAction(options) && !isContentBulkPage(state)
  })
}

/**
 * Persist bulk data for content create to backend
 */
export function persistDataContentBulk ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const {
      storeKey, applicationKey, options, record,
    } = action.payload
    const { config, auth } = state

    // should we be filtering empty fields??
    const content = record.get('content', List()).map((row) => row.withMutations((r) => {
      const published = r.get('publishedAtDate')
      if (published) {
        const d = new Date(published)
        // Adjust the date to be relative to the local timezone
        d.setHours(d.getHours() + new Date().getTimezoneOffset() / 60)
        r.set('publishedAtDate', d.toISOString())
      }
      // TODO Remove this when brooklyn can accept '' as a content reference value
      const references = r.get('references')
      if (references) {
        r.set('references', references.filter((ref) => ref.get('contentId')))
      }
      return r.filter((value) => !!value)
    }))

    try {
      const result = await updateContentList({
        generatePath: options.get('generatePath', false),
        redirectOnUnpublish: options.get('redirectOnUnpublish', false),
        pushToContentReferences: options.get('pushToContentReference', true),
        server: getConfigDataServer(config),
        content,
        auth,
      })
      dispatch(setDataApplicationByKeyAction({
        applicationData: Map({
          content: result.get('content', List()),
          status: result.get('status', List()),
          source: record.get('source', List()),
          state: CONTENT_BULK_STATE_PROCESSED,
        }),
        applicationKey,
        storeKey,
      }))
    } catch (e) {
      dispatch(setDataApplicationByKeyAction({
        applicationData: record.set('error', 'Request failed.')
          .set('state', CONTENT_BULK_STATE_UNPROCESSED),
        applicationKey,
        storeKey,
      }))
    }
  }).when((options = {}) => {
    const { state } = options
    return isContentDataAction(options)
      && isContentBulkPage(state)
  })
}

/**
 * Initialize content detail page
 * @param {*} param0 The params
 */
export function initContentCreatePage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = CONTENT_STORE_KEY
      const applicationKey = CONTENT_CREATE_APPLICATION_KEY
      const options = createContentCreateViewOptions(state)
      const prevOptions = createContentCreateViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const dataReady = view && options.equals(prevOptions)
      if (dataReady) {
        return
      }
      const applidationData = getApplicationDataByKey({
        data, storeKey, applicationKey,
      }) || Map()
      if (!applidationData.isEmpty()) {
        // Clear out any previous application data
        dispatch(deleteDataApplicationByKeyAction({
          storeKey, applicationKey,
        }))
      }
      if (options.get('contentId') && options.get('contentType')) {
        dispatch(getDataListAction({
          storeKey,
          options,
        }))
      }
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isContentCreatePage(state)
  })
}

/**
 * Handle set data list on content create to initialize application data
 * @param {*} param0 The params
 */
export function setDataListContentCreate ({ after }) {
  return after([SET_DATA_LIST], ({ dispatch, action }) => {
    const { storeKey, records } = action.payload
    const record = records.find((v) => v.get('language') === v.get('primaryLanguage'))
    if (record) {
      const applicationData = createContentTranslation(record)
      dispatch(setDataApplicationByKeyAction({
        storeKey,
        applicationKey: CONTENT_CREATE_APPLICATION_KEY,
        applicationData,
      }))
    }
  }).when((options = {}) => {
    const { state } = options
    return isContentDataAction(options) && isContentCreatePage(state)
  })
}

/**
 * Handle the set data application for a create store key
 */
export function setDataApplicationByKeyActionContentCreate ({ after }) {
  return after(SET_DATA_APPLICATION_BY_KEY, async ({
    state, prevState, action, dispatch,
  }) => {
    const { data } = state
    const { data: dataPrev } = prevState
    const { storeKey, applicationKey } = action.payload
    if (applicationKey !== 'create') {
      return
    }
    const applicationData = getApplicationDataByKey({
      data,
      storeKey,
      applicationKey,
    }) || Map()
    const applicationDataPrev = getApplicationDataByKey({
      data: dataPrev,
      storeKey,
      applicationKey,
    }) || Map()
    const canGeneratePath = applicationData.get('title') && applicationData.get('contentType') && applicationData.get('language')
    const dataChanged = Set(['title', 'contentType', 'language']).some((k) => applicationData.get(k) !== applicationDataPrev.get(k))
    if (canGeneratePath && dataChanged) {
      dispatch(getDataListAction({
        storeKey: CONTENT_PATH_STORE_KEY,
        options: createContentPathCreateViewOptions({ content: applicationData }),
      }))
    }
  })
    .when((options = {}) => {
      const { state } = options
      return isContentDataAction(options) && isContentCreatePage(state)
    })
}

export function updateContentReferenceRecords ({ after }) {
  return after(SET_DATA_APPLICATION_BY_KEY, async ({ action, dispatch }) => {
    const { payload: { storeKey, applicationData } } = action
    const content = applicationData.get('references', List()).map((r) => Map({
      contentId: r.get('contentId'),
      contentType: r.get('contentType'),
    })).toSet().toList()
    if (!content.isEmpty()) {
      dispatch(getDataListAction({
        storeKey,
        options: Map({ content, primaryLanguageFallback: true }),
      }))
    }
  })
    .when((options = {}) => isContentDetailDataAction(options))
}

/**
 * Returns true if the action has a storeKey of 'contentPath'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isContentPathDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === CONTENT_PATH_STORE_KEY
}

/**
 * Handle the get data list for a content path action
 * @param {Object} param0 The params
 */
export function getDataListContentPath ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options } = action.payload
    try {
      const path = await getContentPathFromContentTitle({
        auth,
        server: getConfigDataServer(config),
        data: options,
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(List([path]), 'path'),
        total: 1,
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isContentPathDataAction)
}
